import { AlertTriangle } from "lucide-react";
import { AxiosError } from "axios";

import { Button } from "@/components/ui/button/button";

type QueryErrorProps = {
  /** What failed to load, e.g. "employees" — used in the message. */
  what: string;
  error: unknown;
  onRetry?: () => void;
};

/**
 * Distinguishes "the request failed" from "there is nothing here".
 *
 * Without this, a 401/403/5xx renders exactly like an empty list, so a broken
 * or wrong-account session looks identical to genuinely having no data.
 */
export const QueryError = ({ what, error, onRetry }: QueryErrorProps) => {
  const isAxios = error instanceof AxiosError;
  const status = isAxios ? error.response?.status : undefined;
  const detail =
    (isAxios ? (error.response?.data?.error as string | undefined) : undefined) ??
    (error instanceof Error ? error.message : undefined);

  const isAuth = status === 401 || status === 403;

  return (
    <div
      role="alert"
      className="mt-5 max-w-xl rounded-md border border-destructive/40 bg-destructive/5 p-4"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 shrink-0 text-destructive" />
        <div className="space-y-1">
          <p className="font-medium">Couldn&apos;t load {what}</p>
          <p className="text-sm text-muted-foreground">
            {isAuth
              ? "Your session isn't valid for this account. Try logging out and logging back in."
              : detail || "Something went wrong while contacting the server."}
            {status ? ` (HTTP ${status})` : null}
          </p>
          {onRetry && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={onRetry}
            >
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
